import { Injectable } from '@angular/core';
import { filter, map, Observable, Subject } from 'rxjs';
import { AppEvent } from '@core/modules/event-bus/events/app-event';

/**
 * Global event bus service for application-wide event communication.
 *
 * Provides a centralized, type-safe way to emit and listen to events across the application
 * without creating tight coupling between components and services.
 *
 * Events are defined as classes that extend AppEvent, providing strong typing and
 * compile-time safety for event payloads.
 *
 * @example
 * ```typescript
 * // Emit event without payload
 * eventBus.emit(new AppEvents.Sidebar.Closed());
 *
 * // Emit event with required payload
 * eventBus.emit(new AppEvents.Sidebar.Toggled({
 *   collapsed: true,
 *   reason: 'user-action'
 * }));
 *
 * // Listen to specific event type
 * eventBus.on(AppEvents.Sidebar.Closed).subscribe(event => {
 *   console.log('Sidebar closed at:', event.timestamp);
 * });
 *
 * // Listen to event type by string pattern
 * eventBus.onType('sidebar:*').subscribe(event => {
 *   console.log('Sidebar event:', event.type);
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class EventBus {
  /** PROPERTIES **/

  /** Internal event stream for all events */
  private readonly _eventStream$ = new Subject<AppEvent<any>>();

  /** Observable stream of all events */
  readonly events$: Observable<AppEvent<any>> =
    this._eventStream$.asObservable();

  /**
   * Emit an event to all subscribers
   *
   * @param event - Event instance to emit
   *
   * @example
   * ```typescript
   * // Emit event without payload
   * eventBus.emit(new AppEvents.Sidebar.Closed());
   *
   * // Emit event with payload
   * eventBus.emit(new AppEvents.User.Login({ userId: 123, username: 'john' }));
   * ```
   */
  emit<T extends AppEvent<any>>(event: T): void {
    this._eventStream$.next(event);
  }

  /**
   * Listen to events of a specific event class type
   *
   * @param eventClass - Event class constructor to listen for
   * @returns Observable of matching events
   *
   * @example
   * ```typescript
   * // Listen to specific event class
   * eventBus.on(AppEvents.Sidebar.Closed).subscribe(event => {
   *   console.log('Sidebar closed');
   * });
   *
   * // Listen with typed payload
   * eventBus.on(AppEvents.Sidebar.Toggled).subscribe(event => {
   *   console.log('Collapsed:', event.payload.collapsed);
   * });
   * ```
   */
  on<T extends AppEvent<any>>(
    eventClass: new (...args: any[]) => T
  ): Observable<T> {
    return this.events$.pipe(
      filter(event => event instanceof eventClass),
      map(event => event as T)
    );
  }

  /**
   * Listen to events by type string pattern (supports wildcards)
   *
   * @param typePattern - Event type pattern to match (supports '*' wildcard)
   * @returns Observable of matching events
   *
   * @example
   * ```typescript
   * // Listen to specific type
   * eventBus.onType('sidebar:closed').subscribe(event => {
   *   console.log('Sidebar closed');
   * });
   *
   * // Listen to pattern with wildcard
   * eventBus.onType('sidebar:*').subscribe(event => {
   *   console.log('Any sidebar event:', event.type);
   * });
   *
   * // Listen to all events
   * eventBus.onType('*').subscribe(event => {
   *   console.log('Any event:', event.type);
   * });
   * ```
   */
  onType<T = any>(typePattern: string): Observable<AppEvent<T>> {
    return this.events$.pipe(
      filter(event => this._matchesEventType(event.type, typePattern)),
      map(event => event as AppEvent<T>)
    );
  }

  /**
   * Match event type against pattern (supports wildcards)
   *
   * @param eventType - The actual event type
   * @param pattern - The pattern to match against (can include '*' wildcard)
   * @returns True if event type matches the pattern
   *
   * @private
   */
  private _matchesEventType(eventType: string, pattern: string): boolean {
    // Match all events
    if (pattern === '*') {
      return true;
    }

    // Exact match
    if (pattern === eventType) {
      return true;
    }

    // Wildcard pattern matching
    if (pattern.includes('*')) {
      const regex = new RegExp(
        '^' + pattern.replace(/\*/g, '.*').replace(/:/g, '\\:') + '$'
      );
      return regex.test(eventType);
    }

    return false;
  }
}
